(() => {
  const selector = "[data-vehicle-finder]";
  const vinPattern = /^[A-HJ-NPR-Z0-9]{17}$/;
  const normalizeVin = (value) => String(value || "").trim().replace(/[\s-]+/g, "").toUpperCase();

  const initialize = (root) => {
    if (root.dataset.vehicleFinderReady === "true") return;
    root.dataset.vehicleFinderReady = "true";

    const form = root.querySelector("[data-vehicle-finder-form]");
    const input = root.querySelector("[data-vehicle-finder-input]");
    const submit = root.querySelector("[data-vehicle-finder-submit]");
    const status = root.querySelector("[data-vehicle-finder-status]");
    const result = root.querySelector("[data-vehicle-finder-result]");
    const vehicle = root.querySelector("[data-vehicle-finder-vehicle]");
    const resultsLink = root.querySelector("[data-vehicle-finder-results-link]");
    const resultsLabel = root.querySelector("[data-vehicle-finder-results-label]");
    if (!form || !input || !submit || !status || !result || !vehicle || !resultsLink || !resultsLabel) return;

    let modelCatalog = [];
    const normalizeModel = (value) => String(value || "").toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim();
    const matchesYear = (item, year) => item.startYear <= year && item.endYear >= year;
    const phraseMatches = (source, phrase) => ` ${source} `.includes(` ${phrase} `);
    const findCollectionMatch = ({ year, make, model }) => {
      const normalizedMake = normalizeModel(make);
      const normalizedModel = normalizeModel(model);
      const numericYear = Number(year);
      if (!normalizedMake || !normalizedModel || !numericYear) return null;
      const scored = modelCatalog
        .filter((item) => normalizeModel(item.make) === normalizedMake && matchesYear(item, numericYear))
        .map((item) => {
          const scores = item.aliases.map((alias) => {
            const normalizedAlias = normalizeModel(alias);
            if (!normalizedAlias) return 0;
            if (normalizedModel === normalizedAlias) return 1000 + normalizedAlias.length;
            if (phraseMatches(normalizedModel, normalizedAlias)) return 700 + normalizedAlias.length;
            if (phraseMatches(normalizedAlias, normalizedModel)) return 500 + normalizedModel.length;
            return 0;
          });
          const matchScore = Math.max(0, ...scores);
          const exactCollectionBoost = item.matchKind === "exact" ? 10000 : 0;
          return { item, score: matchScore ? matchScore + exactCollectionBoost : 0 };
        })
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score);
      if (!scored.length || (scored[1] && scored[0].score === scored[1].score)) return null;
      return scored[0].item;
    };

    const getFamilyDestination = ({ make, model }) => {
      if (normalizeModel(make) !== "BMW") return null;
      const normalizedModel = normalizeModel(model);
      if (/\bG\d{2}\b/.test(normalizedModel) && root.dataset.gChassisLink) {
        return { url: root.dataset.gChassisLink, title: "BMW G chassis", matchKind: "family" };
      }
      if (/\bF\d{2}\b/.test(normalizedModel) && root.dataset.fChassisLink) {
        return { url: root.dataset.fChassisLink, title: "BMW F chassis", matchKind: "family" };
      }
      if (root.dataset.bmwUniversalLink) {
        return { url: root.dataset.bmwUniversalLink, title: "BMW broad-fitment parts", matchKind: "universal" };
      }
      return null;
    };

    const findVehicleDestination = (selected) => findCollectionMatch(selected) || getFamilyDestination(selected);

    const setDestinationMessage = (destination, statusElement) => {
      if (destination?.matchKind === "family") {
        statusElement.textContent = root.dataset.familyMatchMessage || "This BMW does not need its own collection. Showing parts that fit its chassis family.";
      } else if (destination?.matchKind === "universal") {
        statusElement.textContent = root.dataset.universalMatchMessage || "Showing BMW parts with broad fitment. Review each product's compatibility notes.";
      } else {
        statusElement.textContent = "";
      }
    };

    const setLoading = (loading) => {
      submit.disabled = loading;
      submit.setAttribute("aria-busy", String(loading));
      root.classList.toggle("is-loading", loading);
    };
    const showError = (message) => {
      result.hidden = true;
      input.setAttribute("aria-invalid", "true");
      status.textContent = message || "";
    };
    const showVehicle = (decoded) => {
      const selected = {
        vin: decoded.vin,
        year: decoded.year,
        make: decoded.make,
        model: decoded.model,
        source: "vin",
      };
      const vehicleName = [selected.year, selected.make, selected.model].filter(Boolean).join(" ");
      const collectionMatch = findVehicleDestination(selected);
      vehicle.textContent = vehicleName;
      if (collectionMatch) {
        resultsLink.href = collectionMatch.url;
        resultsLabel.textContent = collectionMatch.matchKind === "exact"
          ? (resultsLabel.dataset.defaultLabel || resultsLabel.textContent)
          : (root.dataset.familyMatchButton || resultsLabel.dataset.defaultLabel || resultsLabel.textContent);
        setDestinationMessage(collectionMatch, status);
        selected.collection = collectionMatch.url;
        selected.matchKind = collectionMatch.matchKind;
      } else {
        resultsLink.href = root.dataset.noCollectionLink || "/collections";
        resultsLabel.textContent = root.dataset.noCollectionButton || "Choose a model manually";
        status.textContent = root.dataset.noCollectionMessage || "We could not match that VIN to one exact collection. Please choose the model manually.";
      }
      result.hidden = false;
      input.setAttribute("aria-invalid", "false");
      try {
        window.localStorage.setItem("vehicle-fitment:selected-vehicle", JSON.stringify(selected));
      } catch (_) {}
      document.dispatchEvent(new CustomEvent("vehicle-fitment:vehicle-selected", { detail: selected }));
    };

    const modelSelector = root.querySelector("[data-vehicle-model-selector]");
    if (modelSelector) {
      const yearSelect = modelSelector.querySelector("[data-ymm-year]");
      const makeSelect = modelSelector.querySelector("[data-ymm-make]");
      const modelSelect = modelSelector.querySelector("[data-ymm-model]");
      const matchLink = modelSelector.querySelector("[data-ymm-result]");
      const matchName = modelSelector.querySelector("[data-ymm-result-name]");
      const selectorStatus = modelSelector.querySelector("[data-ymm-status]");
      const rows = [...root.querySelectorAll("[data-vehicle-model-list] .vehicle-finder-model-option")];
      const currentYear = new Date().getFullYear();
      modelCatalog = rows.map((row) => {
        const yearRange = row.dataset.yearRange || "";
        const years = yearRange.match(/\d{4}/g) || [];
        const startYear = Number(years[0] || currentYear);
        const endYear = /present/i.test(yearRange) ? currentYear : Number(years[1] || years[0] || currentYear);
        return {
          startYear,
          endYear,
          make: row.dataset.make || "",
          model: row.dataset.model || "",
          aliases: (row.dataset.modelAliases || row.dataset.model || "").split("|").map((value) => value.trim()).filter(Boolean),
          title: row.querySelector("strong")?.textContent.trim() || "",
          url: row.getAttribute("href") || "",
          matchKind: row.dataset.matchKind || "exact",
        };
      }).filter((item) => item.make && item.model && item.url);
      modelCatalog = [...new Map(modelCatalog.map((item) => [`${item.url}|${item.model}`, item])).values()];

      const setOptions = (select, values, placeholder) => {
        select.innerHTML = "";
        const first = document.createElement("option");
        first.value = "";
        first.textContent = placeholder;
        select.append(first);
        values.forEach((value) => {
          const option = document.createElement("option");
          option.value = String(value);
          option.textContent = String(value);
          select.append(option);
        });
      };
      const unique = (values) => [...new Set(values)].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
      const resetMatch = () => {
        matchLink.hidden = true;
        matchLink.removeAttribute("href");
        matchName.textContent = "";
        selectorStatus.textContent = "";
      };

      const years = [];
      modelCatalog.forEach((item) => {
        for (let year = item.startYear; year <= item.endYear; year += 1) years.push(year);
      });
      setOptions(yearSelect, unique(years).sort((a, b) => b - a), yearSelect.options[0]?.textContent || "Select year");

      yearSelect.addEventListener("change", () => {
        const year = Number(yearSelect.value);
        const makes = year ? unique(modelCatalog.filter((item) => matchesYear(item, year)).map((item) => item.make)) : [];
        const makePlaceholder = makeSelect.options[0]?.textContent || "Select make";
        const modelPlaceholder = modelSelect.options[0]?.textContent || "Select model";
        setOptions(makeSelect, makes, makePlaceholder);
        setOptions(modelSelect, [], modelPlaceholder);
        makeSelect.disabled = !year;
        modelSelect.disabled = true;
        resetMatch();
      });

      makeSelect.addEventListener("change", () => {
        const year = Number(yearSelect.value);
        const make = makeSelect.value;
        const models = year && make ? unique(modelCatalog.filter((item) => matchesYear(item, year) && item.make === make).map((item) => item.model)) : [];
        const modelPlaceholder = modelSelect.options[0]?.textContent || "Select model";
        setOptions(modelSelect, models, modelPlaceholder);
        modelSelect.disabled = !make;
        resetMatch();
      });

      modelSelect.addEventListener("change", () => {
        const year = Number(yearSelect.value);
        const selected = {
          year,
          make: makeSelect.value,
          model: modelSelect.value,
          source: "manual",
        };
        const match = findVehicleDestination(selected);
        if (!match) {
          resetMatch();
          if (modelSelect.value) selectorStatus.textContent = "No configured collection matches that vehicle.";
          return;
        }
        matchLink.href = match.url;
        matchName.textContent = `${year} ${match.title || `${selected.make} ${selected.model}`}`;
        matchLink.hidden = false;
        setDestinationMessage(match, selectorStatus);
        selected.collection = match.url;
        selected.matchKind = match.matchKind;
        try {
          window.localStorage.setItem("vehicle-fitment:selected-vehicle", JSON.stringify(selected));
        } catch (_) {}
        document.dispatchEvent(new CustomEvent("vehicle-fitment:vehicle-selected", { detail: selected }));
      });
    }

    input.addEventListener("input", () => {
      input.value = normalizeVin(input.value).slice(0, 17);
      input.setAttribute("aria-invalid", "false");
      status.textContent = "";
      result.hidden = true;
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      const vin = normalizeVin(input.value);
      if (!vin) {
        showError(root.dataset.requiredMessage);
        input.focus();
        return;
      }
      if (!vinPattern.test(vin)) {
        showError(root.dataset.invalidMessage);
        input.focus();
        return;
      }

      setLoading(true);
      status.textContent = "";
      result.hidden = true;
      try {
        const endpoint = new URL(root.dataset.endpoint, window.location.origin);
        endpoint.searchParams.set("vin", vin);
        const response = await fetch(endpoint, { headers: { Accept: "application/json" }, credentials: "same-origin" });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.ok) {
          const message = data?.code === "invalid_vin"
            ? root.dataset.invalidMessage
            : data?.code === "not_found"
              ? root.dataset.notFoundMessage
              : data?.code === "rate_limited"
                ? root.dataset.rateLimitedMessage
                : root.dataset.unavailableMessage;
          showError(message);
          return;
        }
        showVehicle({ vin, ...data.vehicle });
      } catch (_) {
        showError(root.dataset.unavailableMessage);
      } finally {
        setLoading(false);
      }
    });
  };

  const initializeAll = (scope = document) => scope.querySelectorAll(selector).forEach(initialize);
  initializeAll();
  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();

